import { CardRow } from 'components/CardRow';
import { ICONS } from 'components/Icon';
import { ModalScreens, Screens } from 'components/Navigation/types';
import { useSelector } from 'hooks/useSelector';
import { isEqual } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { Chip } from 'screens/AssessmentScreen/Finding/Chip';
import { Finding } from 'screens/AssessmentScreen/Finding/Finding';
import { NAVIGATE_MODAL } from 'store/app/actions';
import {
  selectAseActionAstAse,
  selectAseCategories,
  selectAseTimescales,
  selectAseSeverities,
  selectAseTaskStatuses,
  selectAseTplSuggestedAction,
  selectAseExplorerOptionFindingReference,
  selectAseExplorerOptionSize,
  selectAseExplorerOptionFindingDescriptions,
  selectAseExplorerOptionFindingAdditional,
  selectAseExplorerOptionFindingSuggestionDescriptions,
  selectAseExplorerOptionFindingSuggestionAdditional,
} from 'store/assessments/selectors';
import { returnDefaultAction } from 'store/assessments/templates';
import {
  AssessmentItemTypes,
  InstanceParentParams,
} from 'store/assessments/types';

import { Highlight } from 'components/Highlight';
import { doAseDeleteAction, doAseOpenAction } from 'store/assessments/actions';
import { rbAddReferencePrefix } from 'lib/riskbase';
import { useConfirm } from 'hooks/useConfirm';
import { theme } from 'lib/theme';
import { Txt } from 'components/Txt';

export const ExplorerActionRow: React.FC<{
  id: string;
  parent: InstanceParentParams;
}> = React.memo(({ id, parent }) => {
  const dispatch = useDispatch();
  const confirm = useConfirm();
  const explorerOptionFindingDescriptions = useSelector(
    selectAseExplorerOptionFindingDescriptions,
  );
  const explorerOptionFindingAdditional = useSelector(
    selectAseExplorerOptionFindingAdditional,
  );
  const explorerOptionFindingRef = useSelector(
    selectAseExplorerOptionFindingReference,
  );
  const explorerOptionSize = useSelector(selectAseExplorerOptionSize);
  const categories = useSelector(selectAseCategories);
  const timescales = useSelector(selectAseTimescales);
  const severities = useSelector(selectAseSeverities);
  const statuses = useSelector(selectAseTaskStatuses);
  const action = useSelector(
    (state) => selectAseActionAstAse(state, id),
    isEqual,
  );

  if (!action) {
    return null;
  }

  const milestone = !!action.milestone && action.milestone + 1000 > Date.now();
  const severity = severities[action.severityId] ?? severities['0']!;
  const timescale = timescales[action.timescaleId] ?? timescales['0']!;
  const category = categories[action.categoryId] ?? categories['0']!;

  const chips = (explorerOptionFindingRef ||
    (explorerOptionFindingAdditional &&
      (category.id !== '0' ||
        severity.id !== '0' ||
        timescale.id !== '0' ||
        !!action.where ||
        !!action.photoIds.length))) && (
    <>
      {explorerOptionFindingRef && (
        <Chip size={explorerOptionSize} iconSource={ICONS.RBPlectrumSolid}>
          {action.reference ? (
            `${rbAddReferencePrefix(action.reference)}`
          ) : (
            <Txt size="xxxsmall" weight="bold">
              {rbAddReferencePrefix('')}
              <Txt
                size="xxxsmall"
                weight="bold"
                style={{ color: theme.colors.placeholder }}
              >
                000000
              </Txt>
            </Txt>
          )}
        </Chip>
      )}
      {explorerOptionFindingRef && !!action.customReference && (
        <Chip size={explorerOptionSize} iconSource={ICONS.HashtagLight}>
          {action.customReference}
        </Chip>
      )}
      {explorerOptionFindingAdditional && severity.id !== '0' && (
        <Chip
          size={explorerOptionSize}
          iconSource={ICONS.DiamondExclamationSolid}
          color={`#${severity.colour}`}
        >
          {severity.name}
        </Chip>
      )}
      {explorerOptionFindingAdditional && timescale.id !== '0' && (
        <Chip size={explorerOptionSize} iconSource={ICONS.HourglassLight}>
          {timescale.name}
        </Chip>
      )}
      {explorerOptionFindingAdditional && category.id !== '0' && (
        <Chip size={explorerOptionSize} iconSource={ICONS.TagLight}>
          {category.name}
        </Chip>
      )}
      {explorerOptionFindingAdditional && !!action.where && (
        <Chip size={explorerOptionSize} iconSource={ICONS.LocationArrowLight}>
          {action.where}
        </Chip>
      )}
      {explorerOptionFindingAdditional && !!action.photoIds.length && (
        <Chip size={explorerOptionSize} iconSource={ICONS.ImageLight} />
      )}
    </>
  );

  return (
    <Highlight highlight={milestone}>
      <CardRow
        compact={explorerOptionSize === 'small'}
        small={explorerOptionSize === 'small'}
        navigationIsSelected={
          action.fieldInstanceId
            ? { name: Screens.ASEREPEATABLE_ACTION, params: { id, parent } }
            : { name: Screens.ASE_ACTION, params: { id, parent } }
        }
        onPress={() => dispatch(doAseOpenAction({ id, parent }))}
        hideMenuButton
        menuOptions={
          !action.isReview || action.reviewStatusId !== null
            ? [
                {
                  text: action.isReview ? 'Delete Review' : 'Delete Action',
                  destructive: true,
                  icon: ICONS.TrashCanLight,
                  onPress: () =>
                    confirm({
                      text: action.isReview
                        ? 'Are you sure you want to delete the review for this action?'
                        : 'Are you sure you want to delete this action?',
                      title: action.isReview
                        ? 'Delete Review'
                        : 'Delete Action',
                      confirmText: 'Delete',
                      onConfirm: () =>
                        dispatch(doAseDeleteAction({ id: action.id })),
                      destructive: true,
                    }),
                },
              ]
            : undefined
        }
      >
        <Finding
          isUnreviewed={action.isReview && action.reviewStatusId === null}
          superscript={
            action.reviewStatusId
              ? `Reviewed as ${
                  statuses.find((status) => status.id === action.reviewStatusId)
                    ?.name
                }`
              : undefined
          }
          description={action.description}
          why={action.why}
          chips={chips}
          type={AssessmentItemTypes.ACTION}
          descriptionNumberOfLines={
            explorerOptionFindingDescriptions === 'short' ? 2 : 0
          }
          size={explorerOptionSize}
          cancelled={action.reviewStatusId === '4'}
        />
      </CardRow>
    </Highlight>
  );
});

export const AddCustomAction: React.FC<{
  parentId: string;
  parentType: 'section' | 'question';
  fieldInstanceId: string | null;
  parent: InstanceParentParams;
}> = ({ parentId, parentType, fieldInstanceId, parent }) => {
  const dispatch = useDispatch();
  const explorerOptionSize = useSelector(selectAseExplorerOptionSize);
  return (
    <CardRow
      onPress={() => {
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.AseAction,
            params: {
              id: '',
              parent,
              data: returnDefaultAction({
                sectionId: parentType === 'section' ? parentId : null,
                questionId: parentType === 'question' ? parentId : null,
                fieldInstanceId,
                suggestionId: null,
              }),
            },
          }),
        );
      }}
      compact={explorerOptionSize === 'small'}
    >
      <Finding
        description="Add Action"
        why=""
        type={AssessmentItemTypes.ACTION}
        size={explorerOptionSize}
        isAddCustom
      />
    </CardRow>
  );
};

export const AddTemplateAction: React.FC<{
  templateId: string;
  parentId: string;
  parentType: 'section' | 'question';
  fieldInstanceId: string | null;
  parent: InstanceParentParams;
}> = ({ templateId, parentId, parentType, fieldInstanceId, parent }) => {
  const dispatch = useDispatch();
  const explorerOptionFindingDescriptions = useSelector(
    selectAseExplorerOptionFindingSuggestionDescriptions,
  );
  const explorerOptionFindingAdditional = useSelector(
    selectAseExplorerOptionFindingSuggestionAdditional,
  );
  const explorerOptionSize = useSelector(selectAseExplorerOptionSize);
  const categories = useSelector(selectAseCategories);
  const timescales = useSelector(selectAseTimescales);
  const severities = useSelector(selectAseSeverities);
  const action = useSelector((state) =>
    selectAseTplSuggestedAction(state, templateId, fieldInstanceId),
  );

  if (!action) {
    return null;
  }

  const severity = severities[action.severityId || '0'] ?? severities['0']!;
  const timescale = timescales[action.timescaleId] ?? timescales['0']!;
  const category = categories[action.categoryId] ?? categories['0']!;

  const chips = explorerOptionFindingAdditional &&
    (category.id !== '0' || severity.id !== '0' || timescale.id !== '0') && (
      <>
        {explorerOptionFindingAdditional && severity.id !== '0' && (
          <Chip
            size={explorerOptionSize}
            iconSource={ICONS.DiamondExclamationSolid}
            color={`#${severity.colour}`}
          >
            {severity.name}
          </Chip>
        )}
        {explorerOptionFindingAdditional && timescale.id !== '0' && (
          <Chip size={explorerOptionSize} iconSource={ICONS.HourglassLight}>
            {timescale.name}
          </Chip>
        )}
        {explorerOptionFindingAdditional && category.id !== '0' && (
          <Chip size={explorerOptionSize} iconSource={ICONS.TagLight}>
            {category.name}
          </Chip>
        )}
      </>
    );

  return (
    <CardRow
      compact={explorerOptionSize === 'small'}
      onPress={() => {
        dispatch(
          NAVIGATE_MODAL({
            screen: ModalScreens.AseAction,
            params: {
              id: '',
              parent,
              data: {
                ...returnDefaultAction({
                  questionId: parentType === 'question' ? parentId : null,
                  sectionId: parentType === 'section' ? parentId : null,
                  suggestionId: action.id,
                  fieldInstanceId,
                }),
                description: action.description,
                why: action.why,
                categoryId: action.categoryId,
                timescaleId: action.timescaleId,
                severityId: action.severityId || '0',
                dueDeterminedBy: action.dueDeterminedBy,
                dueTimescale: action.dueTimescale,
                dueTypeId: action.dueTypeId,
                cost: action.cost || '',
              },
            },
          }),
        );
      }}
    >
      <Finding
        description={action.description}
        why={action.why}
        chips={chips}
        type={AssessmentItemTypes.ACTION}
        isSuggestion
        countSuggestions={action.usedCount}
        size={explorerOptionSize}
        descriptionNumberOfLines={
          explorerOptionFindingDescriptions === 'short' ? 2 : 0
        }
      />
    </CardRow>
  );
};
